import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ArrangeableTile } from '../api/dashboard'
import { tintOf, titleOf } from '../api/dashboard'

/**
 * Arranging the dashboard's tiles (D-879, D-883).
 *
 * **`@dnd-kit` rather than the browser's own drag-and-drop**, which does not
 * fire on touch at all — and this is a screen she may well open on a phone.
 * Hand-rolling the touch path means pointer events, telling a drag from a
 * scroll, auto-scrolling near the edges and a keyboard route for anybody who
 * cannot drag; that is the part that is genuinely hard and it is already
 * solved. `KeyboardSensor` is why the grip is a real button.
 *
 * **Every tile appears here, including ones with nothing in them today.** The
 * dashboard omits a tile counting zero (D-882), so a list built from what is on
 * screen could never position the tile that is quiet this week — she would be
 * arranging a different set each time she opened it.
 */
export default function DashboardArrange({
  tiles,
  onChange,
}: {
  tiles: ArrangeableTile[]
  onChange: (next: ArrangeableTile[]) => void
}) {
  const sensors = useSensors(
    // A few pixels before a drag starts, so tapping the hide button on a tile
    // is not read as the beginning of a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function dropped(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const from = tiles.findIndex(t => t.key === active.id)
    const to = tiles.findIndex(t => t.key === over.id)
    if (from < 0 || to < 0) return

    onChange(arrayMove(tiles, from, to))
  }

  function toggle(key: string) {
    /*
      Hiding sinks the tile below everything shown, so the list reads in the
      order it will actually render. Without this a hidden tile would keep its
      place and she would be looking at a gap.
    */
    const next = tiles.map(t => (t.key === key ? { ...t, hidden: !t.hidden } : t))
    onChange([...next.filter(t => !t.hidden), ...next.filter(t => t.hidden)])
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dropped}>
      <SortableContext items={tiles.map(t => t.key)} strategy={rectSortingStrategy}>
        <ul className="mt-6 grid w-full list-none grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3 p-0">
          {tiles.map(tile => (
            <ArrangeTile key={tile.key} tile={tile} onToggle={() => toggle(tile.key)} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

function ArrangeTile({ tile, onToggle }: { tile: ArrangeableTile; onToggle: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tile.key,
  })

  const quiet = tile.count === null || tile.count === 0

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : tile.hidden ? 0.55 : 1,
        '--tile-color': tintOf(tile.key),
      } as React.CSSProperties}
      className="w-full"
    >
      <div
        className={`tile-tint relative w-full overflow-hidden rounded-[10px] border bg-surface px-4 py-3.5 pl-[17px] ${
          tile.hidden ? 'border-dashed border-line-2' : 'border-line'
        }`}
      >
        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-[var(--tile-display)]" />

        <span className="block text-[11.5px] font-medium text-ink-2">{titleOf(tile.key)}</span>

        <span
          className={`mt-1.5 block text-[27px] leading-none tracking-tight tabular-nums ${
            quiet ? 'font-medium text-ink-3' : 'font-bold text-ink'
          }`}
        >
          {quiet ? '—' : tile.count}
        </span>

        <span className="mt-1 block text-[11.5px] text-ink-3">
          {quiet ? 'nothing today' : 'on your dashboard'}
        </span>

        <button
          type="button"
          onClick={onToggle}
          aria-pressed={!tile.hidden}
          className="absolute top-2 right-2 rounded-full border border-line-2 bg-surface px-2 py-0.5 text-[11.5px] text-ink-2 hover:border-ink-3"
        >
          {tile.hidden ? 'Hidden' : 'Shown'}
        </button>

        {/*
          A real button, not a decorative handle: `KeyboardSensor` picks a tile
          up with space and moves it with the arrow keys, which is the whole
          reason anybody who cannot drag can still arrange this screen.
        */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Move ${titleOf(tile.key)}`}
          className="absolute right-2 bottom-1.5 cursor-grab touch-none border-0 bg-transparent px-1 text-[13px] text-ink-3 active:cursor-grabbing"
        >
          &#10287;
        </button>
      </div>
    </li>
  )
}
